/**
 * RFP Feature Capability Definitions
 *
 * Defines capability constants for Vision Y Compromiso RFP features.
 * These capabilities can be enabled per-organization through the
 * Capabilities Management admin interface.
 */

// ============================================================================
// CAPABILITY NAMES
// ============================================================================

/**
 * All RFP-related capability names for easy reference
 */
export const RFP_CAPABILITIES = {
  // Phase 1: Accessibility & Role Foundation
  ACCESSIBILITY_SETTINGS: 'rfp_accessibility',
  ROLE_BASED_DASHBOARDS: 'rfp_role_dashboards',

  // Phase 2: Guided Experience System
  GUIDED_TOURS: 'rfp_guided_tours',
  DRAFT_SAVE: 'rfp_draft_save',

  // Phase 3: Communication Translation
  BILINGUAL_TEMPLATES: 'rfp_bilingual_templates',
  SMS_COUNTER: 'rfp_sms_counter',
  TERMINOLOGY_GLOSSARY: 'rfp_terminology_glossary',

  // Phase 4: Mobile & Field Optimization
  OFFLINE_SUPPORT: 'rfp_offline_support',
  MOBILE_OPTIMIZATION: 'rfp_mobile_optimization',
} as const;

export type RFPCapabilityName = typeof RFP_CAPABILITIES[keyof typeof RFP_CAPABILITIES];

// ============================================================================
// CAPABILITY METADATA
// ============================================================================

export interface RFPCapabilityMetadata {
  name: RFPCapabilityName;
  label: string;
  labelEs: string;
  description: string;
  descriptionEs: string;
  category: 'accessibility' | 'experience' | 'communication' | 'mobile';
  phase: 1 | 2 | 3 | 4;
  dependencies?: RFPCapabilityName[];
}

/**
 * Metadata for all RFP capabilities - used for admin UI and documentation
 */
export const RFP_CAPABILITY_METADATA: RFPCapabilityMetadata[] = [
  // Phase 1: Accessibility & Role Foundation
  {
    name: RFP_CAPABILITIES.ACCESSIBILITY_SETTINGS,
    label: 'Accessibility Settings',
    labelEs: 'Configuración de Accesibilidad',
    description: 'WCAG 2.1 AA accessibility features including text scaling, high contrast mode, reduced motion, and screen reader support.',
    descriptionEs: 'Funciones de accesibilidad WCAG 2.1 AA incluyendo escalado de texto, modo de alto contraste, movimiento reducido y soporte para lector de pantalla.',
    category: 'accessibility',
    phase: 1,
  },
  {
    name: RFP_CAPABILITIES.ROLE_BASED_DASHBOARDS,
    label: 'Role-Based Dashboards',
    labelEs: 'Paneles Basados en Roles',
    description: 'Simplified task-focused dashboards for Promotoras and Supervisors with role-specific navigation and permissions.',
    descriptionEs: 'Paneles simplificados enfocados en tareas para Promotoras y Supervisores con navegación y permisos específicos por rol.',
    category: 'accessibility',
    phase: 1,
  },

  // Phase 2: Guided Experience System
  {
    name: RFP_CAPABILITIES.GUIDED_TOURS,
    label: 'Guided Tours',
    labelEs: 'Tours Guiados',
    description: 'Interactive step-by-step walkthroughs for onboarding and feature discovery with role-based tour filtering.',
    descriptionEs: 'Recorridos interactivos paso a paso para incorporación y descubrimiento de funciones con filtrado de tours por rol.',
    category: 'experience',
    phase: 2,
  },
  {
    name: RFP_CAPABILITIES.DRAFT_SAVE,
    label: 'Draft Save & Recovery',
    labelEs: 'Guardar y Recuperar Borradores',
    description: 'Auto-save drafts with recovery prompts to prevent data loss during form completion.',
    descriptionEs: 'Guardado automático de borradores con indicaciones de recuperación para prevenir pérdida de datos durante el llenado de formularios.',
    category: 'experience',
    phase: 2,
  },

  // Phase 3: Communication Translation
  {
    name: RFP_CAPABILITIES.BILINGUAL_TEMPLATES,
    label: 'Bilingual Communication Templates',
    labelEs: 'Plantillas de Comunicación Bilingües',
    description: 'Pre-built English/Spanish communication templates with tone and dialect controls for SMS, email, and in-app messages.',
    descriptionEs: 'Plantillas de comunicación predefinidas en inglés/español con controles de tono y dialecto para SMS, correo electrónico y mensajes en la aplicación.',
    category: 'communication',
    phase: 3,
  },
  {
    name: RFP_CAPABILITIES.SMS_COUNTER,
    label: 'SMS Character Counter',
    labelEs: 'Contador de Caracteres SMS',
    description: 'Real-time SMS segment calculation with GSM-7/Unicode detection and cost optimization suggestions.',
    descriptionEs: 'Cálculo de segmentos SMS en tiempo real con detección GSM-7/Unicode y sugerencias de optimización de costos.',
    category: 'communication',
    phase: 3,
  },
  {
    name: RFP_CAPABILITIES.TERMINOLOGY_GLOSSARY,
    label: 'Terminology Glossary',
    labelEs: 'Glosario de Terminología',
    description: 'Searchable glossary of healthcare and program terms with Spanish translations and usage examples.',
    descriptionEs: 'Glosario de términos de salud y programas con traducciones al español y ejemplos de uso.',
    category: 'communication',
    phase: 3,
  },

  // Phase 4: Mobile & Field Optimization
  {
    name: RFP_CAPABILITIES.OFFLINE_SUPPORT,
    label: 'Offline Support',
    labelEs: 'Soporte Sin Conexión',
    description: 'Service worker caching, offline data access, and background sync for field workers.',
    descriptionEs: 'Almacenamiento en caché del service worker, acceso a datos sin conexión y sincronización en segundo plano para trabajadores de campo.',
    category: 'mobile',
    phase: 4,
  },
  {
    name: RFP_CAPABILITIES.MOBILE_OPTIMIZATION,
    label: 'Mobile Optimization',
    labelEs: 'Optimización Móvil',
    description: 'Touch-optimized UI with 44px minimum touch targets, safe area support, and responsive layouts.',
    descriptionEs: 'Interfaz optimizada para táctil con objetivos táctiles mínimos de 44px, soporte de área segura y diseños responsivos.',
    category: 'mobile',
    phase: 4,
  },
];

// ============================================================================
// CAPABILITY BUNDLES
// ============================================================================

/**
 * Pre-defined capability bundles for common use cases
 */
export const RFP_CAPABILITY_BUNDLES = {
  /** All Phase 1 capabilities */
  PHASE_1_FOUNDATION: [
    RFP_CAPABILITIES.ACCESSIBILITY_SETTINGS,
    RFP_CAPABILITIES.ROLE_BASED_DASHBOARDS,
  ],

  /** All Phase 2 capabilities */
  PHASE_2_EXPERIENCE: [
    RFP_CAPABILITIES.GUIDED_TOURS,
    RFP_CAPABILITIES.DRAFT_SAVE,
  ],

  /** All Phase 3 capabilities */
  PHASE_3_COMMUNICATION: [
    RFP_CAPABILITIES.BILINGUAL_TEMPLATES,
    RFP_CAPABILITIES.SMS_COUNTER,
    RFP_CAPABILITIES.TERMINOLOGY_GLOSSARY,
  ],

  /** All Phase 4 capabilities */
  PHASE_4_MOBILE: [
    RFP_CAPABILITIES.OFFLINE_SUPPORT,
    RFP_CAPABILITIES.MOBILE_OPTIMIZATION,
  ],

  /** CBO essentials - recommended for all CBOs */
  CBO_ESSENTIALS: [
    RFP_CAPABILITIES.ACCESSIBILITY_SETTINGS,
    RFP_CAPABILITIES.ROLE_BASED_DASHBOARDS,
    RFP_CAPABILITIES.GUIDED_TOURS,
    RFP_CAPABILITIES.BILINGUAL_TEMPLATES,
  ],

  /** Field worker package - for Promotoras */
  FIELD_WORKER: [
    RFP_CAPABILITIES.ACCESSIBILITY_SETTINGS,
    RFP_CAPABILITIES.ROLE_BASED_DASHBOARDS,
    RFP_CAPABILITIES.DRAFT_SAVE,
    RFP_CAPABILITIES.OFFLINE_SUPPORT,
    RFP_CAPABILITIES.MOBILE_OPTIMIZATION,
  ],

  /** Full RFP package - all capabilities */
  FULL_RFP: Object.values(RFP_CAPABILITIES),
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get all capability names as an array
 */
export function getAllRFPCapabilityNames(): RFPCapabilityName[] {
  return Object.values(RFP_CAPABILITIES);
}

/**
 * Get metadata for a specific capability
 */
export function getRFPCapabilityMetadata(
  capabilityName: RFPCapabilityName
): RFPCapabilityMetadata | undefined {
  return RFP_CAPABILITY_METADATA.find((m) => m.name === capabilityName);
}

/**
 * Get capabilities by phase
 */
export function getRFPCapabilitiesByPhase(phase: 1 | 2 | 3 | 4): RFPCapabilityMetadata[] {
  return RFP_CAPABILITY_METADATA.filter((m) => m.phase === phase);
}

/**
 * Get capabilities by category
 */
export function getRFPCapabilitiesByCategory(
  category: 'accessibility' | 'experience' | 'communication' | 'mobile'
): RFPCapabilityMetadata[] {
  return RFP_CAPABILITY_METADATA.filter((m) => m.category === category);
}
